import { Clickable } from '@carvana/merch-components';
import { useCallback, useContext, useMemo, useState } from 'react';
import COOKIES from '~/constants/cookies';
import TradeContext from '~/context/common/TradeContext';
import PricingContext from '~/context/srp/PricingContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import DownArrow from '~/icons/DownArrow';
import { BrowserCookies } from '~/utilities';
import { CustomRadio, GreyBar } from './TradeCard.styles';

export type SelectOption = {
  id: any;
  isSelected: boolean;
  ymm: string;
};

export default function MultiTradeSelect() {
  const {
    trades: { data: trades },
    selectedTrade,
    setSelectedTrade,
    isTradeInApplied,
    setTradeInApplied
  } = useContext(TradeContext);
  const { setShouldUpdateTerms } = useContext(PricingContext);
  const [isOpen, setOpen] = useState(false);
  const trackEvent = useTrackEvent();

  const selectedAcquisitionName = selectedTrade?.id ?? '';

  const handleSelect = useCallback(
    (option: SelectOption) => {
      const selectedTradeData = trades.find(t => t.id === option.id);

      //Always set cookie on selection to ensure trades align with the trades that comes back from the api
      if (selectedTradeData) {
        BrowserCookies.set(COOKIES.preferredTrade, selectedTradeData.id, {
          expires: 365
        });
        setSelectedTrade(selectedTradeData);

        //We want to make sure that trade is applied when swapping trades.
        //Toggling the trade will trigger the pricing call see useTradeToggled
        //If trade is already applied, we need to update the shouldUpdateTerms to trigger the pricing call
        if (isTradeInApplied) {
          setShouldUpdateTerms(true);
        } else {
          setTradeInApplied(true);
        }
      }
    },
    [
      isTradeInApplied,
      setSelectedTrade,
      setShouldUpdateTerms,
      setTradeInApplied,
      trades
    ]
  );

  const options = useMemo<SelectOption[]>(
    () =>
      trades.map(t => ({
        ...t,
        id: t.id,
        ymm: `${t.vehicle.year} ${t.vehicle.make} ${t.vehicle.model}`,
        isSelected: t.id === selectedAcquisitionName
      })),
    [selectedAcquisitionName, trades]
  );

  return trades.length > 1 ? (
    <>
      <GreyBar />

      <div className="flex flex-col gap-xl">
        <Clickable
          className="relative mx-auto flex w-fit items-center gap-sm text-blue-2 tl-body-s hover:text-blue-3"
          onClick={() => setOpen(!isOpen)}
          data-analytics-id="Trades Dropdown"
        >
          Switch vehicle <DownArrow className={isOpen ? 'rotate-180' : ''} />
        </Clickable>
        {isOpen ? (
          <>
            {options.map(o => (
              <CustomRadio
                id={o.id}
                key={o.id}
                label={o.ymm}
                checked={selectedAcquisitionName === o.id}
                onClick={() => {
                  trackEvent('Trade Selected');
                  handleSelect(o);
                  setOpen(false);
                }}
              />
            ))}
          </>
        ) : null}
      </div>
    </>
  ) : null;
}
